import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick } from '../config/gtm';

const WavyTop = ({ className = '' }: { className?: string }) => (
    <div className={`w-full overflow-hidden leading-[0] ${className}`} aria-hidden>
        <svg className="relative block w-full h-8 sm:h-10 text-princonser-cream" viewBox="0 0 1440 48" preserveAspectRatio="none">
            <path fill="currentColor" d="M0,32 C360,0 720,48 1080,24 C1260,12 1380,8 1440,16 L1440,48 L0,48 Z" />
        </svg>
    </div>
);

const WavyBottom = () => (
    <div className="w-full overflow-hidden leading-[0]" aria-hidden>
        <svg className="relative block w-full h-6 text-princonser-orange/30" viewBox="0 0 1440 32" preserveAspectRatio="none">
            <path fill="currentColor" d="M0,16 C360,32 720,0 1080,16 C1260,24 1380,8 1440,16 L1440,32 L0,32 Z" />
        </svg>
    </div>
);

const Footer: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const t = (es: string, en: string) => (language === 'es' ? es : en);

    useEffect(() => {
        if (location.hash === '#contacto') {
            document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location.hash]);

    const exploreLinks = [
        { label: t('Home', 'Home'), path: '/', icon: '🏠' },
        { label: t('¿Qué es Princonser Kids?', 'What is Princonser Kids?'), path: '/sobre-nosotros', icon: '👥' },
        { label: t('Blog Princonser', 'Princonser Blog'), path: '/blog-princonser', icon: '📰' },
        { label: t('Libros', 'Books'), path: '/libros/1', icon: '📚' },
        { label: t('Círculo de sabios', 'Circle of Sages'), path: '/consejo-sabios', icon: '🎓' },
        { label: t('Para familias y escuelas', 'For Families and Schools'), path: '/support-materials', icon: '🎁' },
        { label: t('Contacto', 'Contact'), path: '/#contacto', icon: '✉️', isContact: true },
    ];

    const featurePills = [
        { label: t('Libros', 'Books'), path: '/libros/1', emoji: '📚', bg: 'bg-princonser-orange' },
        { label: t('Retos', 'Challenges'), path: '/innovaciones?section=4', emoji: '⭐', bg: 'bg-princonser-gold' },
        { label: t('Insignias', 'Badges'), path: '/gamification', emoji: '🛡️', bg: 'bg-princonser-green' },
        { label: t('App Debate', 'Debate App'), path: '/consejo-sabios', emoji: '💬', bg: 'bg-princonser-purple' },
    ];

    const goContact = () => {
        trackCTAClick('Contactar ahora', 'Footer CTA', 'button');
        if (location.pathname === '/') {
            document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/#contacto');
        }
    };

    return (
        <footer id="contacto" className="text-princonser-navy">
            {/* CTA — crema + naranja (mockup) */}
            <div className="relative bg-princonser-cream overflow-hidden">
                <WavyTop className="text-white" />
                <div className="relative px-4 sm:px-6 lg:px-8 pb-12 sm:pb-14 pt-6">
                    <span className="absolute top-10 left-[8%] text-2xl opacity-30 pointer-events-none" aria-hidden>✈️</span>
                    <span className="absolute top-14 right-[10%] text-xl opacity-30 pointer-events-none" aria-hidden>💡</span>
                    <span className="absolute bottom-14 left-[12%] text-lg opacity-25 pointer-events-none" aria-hidden>❤️</span>
                    <span className="absolute bottom-16 right-[15%] text-2xl opacity-25 pointer-events-none" aria-hidden>📖</span>

                    <div className="max-w-3xl mx-auto text-center relative z-10">
                        <div className="inline-flex items-center justify-center w-10 h-10 mb-4 text-2xl" aria-hidden>👑</div>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight mb-4 text-princonser-navy">
                            {t('Sigamos construyendo', "Let's keep building")}{' '}
                            <span className="text-princonser-orange">{t('sabiduría para los niños', 'wisdom for children')}</span>
                        </h2>
                        <p className="text-sm sm:text-base text-princonser-text-body max-w-xl mx-auto mb-8 leading-relaxed">
                            {t(
                                'Únete a la comunidad Princonser Kids y acompaña a tus hijos en una experiencia educativa llena de propósito, hábitos, retos e inspiración.',
                                'Join the Princonser Kids community and accompany your children in an educational experience full of purpose, habits, challenges and inspiration.'
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={goContact}
                            className="inline-flex items-center gap-2 px-8 py-3.5 sm:px-10 sm:py-4 rounded-full font-bold text-white bg-princonser-orange hover:bg-princonser-orange-dark shadow-princonser-orange transition-all hover:scale-[1.02]"
                        >
                            {t('Contactar ahora', 'Contact now')}
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/25 text-sm">→</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer principal — blanco sobre crema */}
            <div className="bg-princonser-cream px-4 sm:px-6 lg:px-8 pb-6">
                <div className="max-w-7xl mx-auto bg-white border border-orange-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-princonser-card">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8">
                        <div className="space-y-5">
                            <div>
                                <span className="block text-xl font-extrabold text-princonser-purple">PRINCONSER</span>
                                <span className="block text-lg font-extrabold -mt-1">
                                    <span className="text-princonser-purple">K</span>
                                    <span className="text-princonser-purple-light">i</span>
                                    <span className="text-princonser-green">d</span>
                                    <span className="text-princonser-pink">s</span>
                                </span>
                            </div>
                            <p className="text-sm text-princonser-text-body leading-relaxed max-w-xs">
                                {t(
                                    'Formando niños sabios con propósito a través de libros, retos, hábitos, insignias y experiencias educativas basadas en sabiduría.',
                                    'Raising wise children with purpose through books, challenges, habits, badges and wisdom-based educational experiences.'
                                )}
                            </p>
                            <div className="flex flex-wrap gap-4 sm:gap-5">
                                {featurePills.map((item) => (
                                    <button
                                        key={item.label}
                                        type="button"
                                        onClick={() => navigate(item.path)}
                                        className="flex flex-col items-center gap-1.5 group"
                                    >
                                        <div
                                            className={`w-12 h-12 rounded-full ${item.bg} text-white flex items-center justify-center text-lg shadow-md group-hover:scale-105 transition-transform`}
                                        >
                                            {item.emoji}
                                        </div>
                                        <span className="text-xs text-princonser-navy font-semibold">{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-bold text-princonser-orange mb-5">{t('Explora', 'Explore')}</h3>
                            <ul className="space-y-3">
                                {exploreLinks.map((link) => (
                                    <li key={link.path}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (link.isContact) {
                                                    if (location.pathname === '/') {
                                                        document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
                                                    } else {
                                                        navigate('/#contacto');
                                                    }
                                                } else {
                                                    navigate(link.path);
                                                }
                                            }}
                                            className="flex items-center gap-3 text-sm text-princonser-text-body hover:text-princonser-orange font-medium transition-colors w-full text-left"
                                        >
                                            <span className="text-base w-5 text-center">{link.icon}</span>
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-bold text-princonser-orange mb-5">{t('Contacto', 'Contact')}</h3>
                            <div className="space-y-3">
                                <div className="flex gap-3 p-4 rounded-2xl bg-princonser-cream border border-orange-100">
                                    <div className="w-11 h-11 rounded-full bg-princonser-orange text-white flex items-center justify-center flex-shrink-0 text-lg">
                                        🏢
                                    </div>
                                    <div className="text-sm">
                                        <p className="font-bold text-princonser-navy">Multitest Solutions LLC</p>
                                        <p className="text-princonser-text-body mt-1 leading-relaxed">
                                            7901 4th St N Ste 300
                                            <br />
                                            St. Petersburg, FL 33702
                                        </p>
                                    </div>
                                </div>
                                <a
                                    href="tel:+13055185322"
                                    className="flex items-center gap-3 p-4 rounded-2xl bg-princonser-cream border border-orange-100 hover:border-princonser-orange transition-colors"
                                >
                                    <div className="w-11 h-11 rounded-full bg-princonser-orange text-white flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-semibold text-princonser-navy">+1 (305) 518 5322</span>
                                </a>
                                <a
                                    href="mailto:info@PrinconserKids.com"
                                    className="flex items-center gap-3 p-4 rounded-2xl bg-princonser-cream border border-orange-100 hover:border-princonser-orange transition-colors"
                                >
                                    <div className="w-11 h-11 rounded-full bg-princonser-purple text-white flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-semibold text-princonser-navy">info@PrinconserKids.com</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto mt-6 pt-5 border-t border-orange-100 relative">
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-princonser-gold text-xs" aria-hidden>✦</span>
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-princonser-muted">
                        <p className="flex items-center gap-2">
                            <span aria-hidden>👑</span>
                            © 2024 Multitest Solutions LLC. {t('Todos los derechos reservados.', 'All rights reserved.')}
                        </p>
                        <div className="flex items-center gap-6">
                            <a href="/privacidad" className="flex items-center gap-1.5 hover:text-princonser-orange font-medium transition-colors">
                                🔒 {t('Privacidad', 'Privacy')}
                            </a>
                            <span className="text-orange-200">|</span>
                            <a href="/terminos-condiciones" className="hover:text-princonser-orange font-medium transition-colors">
                                {t('Términos y condiciones', 'Terms and conditions')}
                            </a>
                            <span className="text-princonser-pink" aria-hidden>♥</span>
                        </div>
                    </div>
                </div>
                <WavyBottom />
            </div>
        </footer>
    );
};

export default Footer;
